import {
    DefaultButton, Dropdown, IDropdownOption,
    IPanelProps,
    IRenderFunction, MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Separator, Stack, Text,
    TextField,
    Toggle
} from "@fluentui/react";
import { RootState } from "app/store";
import { fadeIn, panelButtonsStyles } from "global/Application";
import { useApplication } from "global/Application/Context";
import { addUpdatePlan } from "global/Reducer/dataSlice";
import { IPanelEvent } from "model/Extension";
import {
    IOptionSetValue as FAM_IOptionSetValue,
    IPlan as FAM_IPlan
} from "model/FlexibleAttributeModel";
import { useEffect, useState } from "react";
import { connect, ConnectedProps, useDispatch } from "react-redux";

interface IData {
    plan: FAM_IPlan | null;
}

interface IProps extends TComponentConnectedProps {
    //TODO: unmount component with delay for animation
    isOpen: boolean;

    // cancellation of transaction/partial actions
    onCancel?: () => void;

    // dialog closure request from parent component
    onDismiss: () => void

    // use this prior to completion of primary action for verification/or to perform action on parent
    onConfirm?: (e?: IPanelEvent<IData>) => void;

    // start condition
    value?: IData;

    variant?: "Summary"
}

const PlanProperties = (props: IProps) => {

    const dispatch = useDispatch();
    const application = useApplication();

    // current view state/stages
    const [validationMessage, setValidationMessage] = useState<string | null>(null);

    // selected category
    const [plan, setPlan] = useState<FAM_IPlan | null>(null);

    useEffect(() => {

        if (props.value?.plan)
            setPlan(structuredClone(props.value.plan));

    }, [props.isOpen]);

    function onRenderAddProperties(): JSX.Element {

        const isGrandFathered = true;
        const isDualFunded = props.state.data.json.dualFunded?.value ?? false;


        if (!plan)
            return <div>Plan not available!</div>;

        return (
            <div className={fadeIn}>
                <Stack tokens={{ childrenGap: 10 }}>

                    <TextField
                        label='Name'
                        value={plan.name ?? ''}
                        onChange={(event, value) => {
                            setPlan(t => { return { ...t!, name: value ?? '' } });
                        }}
                    />

                    <Dropdown
                        label={"Funding Type"}
                        options={props.state.data.config.cache.options_bcbs_fundingtypes ?? []}
                        disabled={!isDualFunded}
                        selectedKey={plan.fundingType?.value}
                        onChange={(_, option?: IDropdownOption<FAM_IOptionSetValue>) => {
                            setPlan(t => { return { ...t!, fundingType: option?.data ?? null } });
                        }}
                    />
                    {!isDualFunded &&
                        <Text styles={{ root: { color: 'gray' } }} variant={"xSmall"}>Group configuration has not been setup for dual funding.</Text>
                    }

                    <Dropdown
                        label={"Grandfathered"}
                        options={props.state.data.config.cache.options_bcbs_grandfatheredcode ?? []}
                        disabled={!isGrandFathered}
                        selectedKey={plan.grandFathered?.value}
                        onChange={(_, option?: IDropdownOption<FAM_IOptionSetValue>) => {
                            setPlan(t => { return { ...t!, grandFathered: option?.data ?? null } });
                        }}
                    />

                    <Toggle
                        label={"Billing Only Plan"}
                        disabled={!application.ChangeTypePermissions.plan.modify_deactivate}
                        checked={plan.billingOnlyPlan ?? false}
                        onChange={(_, checked) => {
                            setPlan(t => { return { ...t!, billingOnlyPlan: checked ?? false }; });
                        }}
                    />

                    <Toggle
                        label={"Deactivate"}
                        disabled={!application.ChangeTypePermissions.plan.modify_deactivate}
                        checked={plan.deactivated ?? false}
                        onChange={(_, checked) => {
                            setPlan(t => { return { ...t!, deactivated: checked ?? false }; });
                        }}
                    />

                </Stack>

            </div>
        );
    }

    function onRenderAddPropertiesFooter(): JSX.Element | null {
        if (!plan)
            return <div>Plan not available!</div>;

        return <div className={fadeIn}>
            <PrimaryButton text='OK'
                styles={panelButtonsStyles}
                disabled={plan.name === ''}
                onClick={() => {
                    dispatch(addUpdatePlan([plan]));
                    props.onDismiss?.();
                }}
            />
            <DefaultButton text='Cancel' onClick={props.onDismiss} />
        </div>;
    }

    const onRenderUnknown: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        return (<div>Unknown Status</div>)
    }

    const onRenderBody: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        let onRender = onRenderUnknown;

        onRender = onRenderAddProperties;

        return (
            <div>
                <Separator />
                {validationMessage &&
                    <MessageBar className={fadeIn} messageBarType={MessageBarType.error} onDismiss={() => setValidationMessage("")}                >
                        {validationMessage}
                    </MessageBar>
                }

                {onRender()}
            </div>
        );
    }

    const onRenderFooterContent: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        let onRender = onRenderUnknown;
        onRender = onRenderAddPropertiesFooter;

        return onRender();
    }

    if (props.variant === "Summary")
        return onRenderAddProperties();

    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            type={PanelType.medium}
            closeButtonAriaLabel="Close"
            headerText="Plan Properties"
            // onRenderHeader={onRenderHeader}
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom={true}
        >
            {onRenderBody()}
        </Panel>
    )
}


function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(PlanProperties);
